import styled from "@emotion/styled";
import {
  Button,
  Col,
  Dropdown,
  MenuProps,
  message,
  Modal,
  Tooltip,
  Typography,
} from "antd";
import { FC } from "react";
import { bankingData } from "services/interface";
import { ExclamationCircleFilled, SettingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import noImage from "assets/images/no-image.png";
import { useDeleteBankFactory, useUpdateBankFactoryStatus } from "hooks/useFactory";
import { numberFormat } from "utils/nuberFormat";
import { useAppSelector } from "store";

interface BankCardProps {
  data: bankingData | undefined;
  refetch: () => void;
  refetchTo?: () => void;
  handleAfterDelete: () => void;
  activeBankCount: number;
  isLunganek: boolean;
}

const { confirm } = Modal;

const BankCard: FC<BankCardProps> = ({data, refetch, refetchTo, activeBankCount, isLunganek }) => {

  const { mutate:deleteBank } = useDeleteBankFactory();
  const { mutate:updateStatus } = useUpdateBankFactoryStatus();
  const navigate = useNavigate();

  const { contact } = useAppSelector((state) => state.auth);

  const cannotActivateWhileOtherActive =
    !isLunganek &&
    data?.bank_status === "inactive" &&
    activeBankCount >= 1;

  const showDeleteConfirm = () => {
    if (!data?.id) return;
    confirm({
      title: "คุณต้องการลบเลขบัญชีนี้ใช่หรือไม่",
      icon: <ExclamationCircleFilled />,
      content: "คุณต้องการลบเลขบัญชีนี้ใช่หรือไม่",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        deleteBank({
          bank_id: data?.id
        }, {
          onSuccess: (data) => {
            if (data.status === "success") {
              message.success("ลบบัญชีสำเร็จ");
              refetch();
            } else {
              message.error("ลบบัญชีไม่สำเร็จ");               
            }
          },
          onError: () => {
            message.error("ลบบัญชีไม่สำเร็จ");
          },
        });
      },
      onCancel() {
        //console.log("Cancel");
      },
    });
  };

  const items: MenuProps["items"] = [
    // {
    //   key: "1",
    //   label: <div>ดูข้อมูล</div>,
    // },
    {
      key: "2",
      label: <div onClick={showDeleteConfirm}>ลบบัญชี</div>,
    },
  ];

  return (
    <Col sm={24} md={12} xl={8} span={24}>
      <CardWrapper>
        <CardContentWrapper onClick={()=> {
          navigate(`view/${data?.id}`)
        }}>
          <ImageWrapper>
            <img
              src={data?.image ?? noImage}
              // src={noImage}
              width={"100%"}
              height={"100%"}
              alt="Product order"
              style={{ objectFit: "cover" }}
            />
          </ImageWrapper>
          <TextWrapper>
            <Typography.Text className="text-order-agent-name">
              {data && data.bank_name && data?.bank_name.length  > 50
                ? data?.bank_name.slice(0, 50) + "..."
                : data?.bank_name}
            </Typography.Text>
            <Typography.Text className="text-order-agent-name">
              {data && data.account_no && data?.account_no.length > 60
                ? data?.account_no.slice(0, 60) + "..."
                : data?.account_no}
            </Typography.Text>
            <Typography.Text className="text-order-agent-name">
              {data?.account_name}
            </Typography.Text>
            { contact?.code === "lunganek" ? (
            <Typography.Text className="text-order-agent-name">
              ยอดรับโอนรวม {numberFormat(data?.bank_paid_total ?? 0)} บาท
            </Typography.Text>
            ) : (
              <>
              </>
            )}
            { contact?.code === "lunganek" ? (
            <Typography.Text className="text-order-agent-name">
             จำนวนครั้งรับโอนรวม {numberFormat(data?.order_count ?? 0)} ราการ
            </Typography.Text>
            ) : (
              <>
              </>
            )}
            <Typography.Text className="text-order-agent-name">
              ประเภท: {data?.bank_type_name} 
            </Typography.Text>
          </TextWrapper>
        </CardContentWrapper>
        <Divider />
        <DateWrapper>
          <Tooltip
            title={
              cannotActivateWhileOtherActive
                ? "มีบัญชีที่เปิดใช้งานอยู่แล้ว"
                : undefined
            }
          >
            <Button
            onClick={() => {
              if (cannotActivateWhileOtherActive) {
                message.warning("มีบัญชีที่เปิดใช้งานอยู่แล้ว");
                return;
              }
              updateStatus(
                {
                  id: data?.id || '',
                  bank_status: data?.bank_status === "active" ? "inactive" : "active",
                }, {
                onSuccess: (data) => {
                  if (data.status === "success") {
                    message.success("แก้ไขสำเร็จ");
                    refetch();
                    if (refetchTo !== undefined) {
                      refetchTo();
                    }
                  } else {
                    message.error("แก้ไขไม่สำเร็จ");               
                  }
                },
                onError: () => {
                  message.error("แก้ไขไม่สำเร็จ");
                },
              });
            }}
            disabled={cannotActivateWhileOtherActive}
            style={{ 
              width: "100%", 
             // color: "#A0A0A0"
             }}
          >
          {data?.bank_status === "active" ? "ปิดการใช้งาน" : "เปิดการใช้งาน"}
          </Button>
          </Tooltip>
          <Button
            onClick={() => {
              navigate(`/manage-bank/update/${data?.id}`);
            }}
            style={{
              width: "100%",
              marginLeft: 10,
              marginRight: 10,
            }}
          >
             <SettingOutlined key="setting" /> แก้ไข
          </Button>
          <Dropdown menu={{ items }} placement="bottomRight">
            <Button
              className="product-btn"
              style={{ 
                width: 50, 
               }}
            >
              ...
            </Button>
          </Dropdown>
        </DateWrapper>
      </CardWrapper>
    </Col>
  );
};

export default BankCard;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 10px;
  background-color: #ffffff;
  border-radius: 0px;
`;

const CardContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`;

const ImageWrapper = styled.div`
  width: 80px;
  height: 80px;
  background-color: #efefef;
  margin-right: 10px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: 110px;
  width: auto;
  border-radius: 0px;
  cursor: pointer;
`;

const Divider = styled.div`
  border-bottom: 0.5px solid #e8e8e8;
  margin: 10px 0px 10px 0px;
`;

const DateWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  text-align: center;
`;
